import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldCategory,
  type FieldValues,
} from 'features/libra/ontology/ontologyFieldTypes'
import type { LibraWorkflowGlobalContext } from 'features/libra/workflow/workflowStepsApi'
import { type ComponentType, createElement, lazy } from 'react'
import type { z } from 'zod'

import { ArrayTableSectionSchema } from './arrayTableSection/arrayTableSectionTypes'
import {
  ComputedSectionObjectSchema,
  getComputedFieldId,
} from './computedSection/computedSectionTypes'
import { ConsequenceOfFailureSectionSchema } from './consequenceOfFailureSection/consequenceOfFailureSectionTypes'
import {
  documentAttachmentIdsSchema,
  DocumentAttachmentsSectionSchema,
} from './documentAttachmentsSection/documentAttachmentsTypes'
import {
  FlowchartSectionSchema,
  getFlowchartFieldId,
} from './flowchartSection/flowchartTypes'
import { FormSectionSchema } from './formSection/formSectionTypes'
import { executionSectionSchema } from './inspectionExecutionSection/executionTypes'
import { isExecutionSectionComplete } from './inspectionExecutionSection/inspectionResultsModel'
import {
  isInsulationPreparationComplete,
  readInsulationPreparationRows,
} from './insulationPreparationSection/insulationPreparationModel'
import {
  type InsulationPreparationSection,
  insulationPreparationSectionSchema,
} from './insulationPreparationSection/insulationPreparationTypes'
import type {
  ArrayTableSection,
  ComputedSection,
  DocumentAttachmentsSection,
  FlowchartSection,
  FormSection,
  SectionRenderProps,
} from './sectionTypes'
import { ZonesSectionSchema } from './zonesSection/zonesSectionTypes'

const definitions = {
  insulation_preparation: {
    schema: insulationPreparationSectionSchema,
    renderer: lazy(async () => {
      const { InsulationPreparationSectionView } =
        await import('./insulationPreparationSection/InsulationPreparationSectionView')
      return { default: InsulationPreparationSectionView }
    }),
    completion: (
      section: InsulationPreparationSection,
      fieldValues: FieldValues,
      globalContext: LibraWorkflowGlobalContext
    ) => {
      const rows = readInsulationPreparationRows(
        section,
        fieldValues,
        globalContext
      )
      return rows.success && isInsulationPreparationComplete(rows.data)
    },
  },
  inspection_execution: {
    schema: executionSectionSchema,
    renderer: lazy(async () => {
      const { ExecutionSectionView } =
        await import('./inspectionExecutionSection/ExecutionSectionView')
      return { default: ExecutionSectionView }
    }),
    completion: isExecutionSectionComplete,
  },
  form: {
    schema: FormSectionSchema,
    renderer: lazy(async () => {
      const [{ ConditionalFormSectionView }, { QuestionListSection }] =
        await Promise.all([
          import('features/libra/stepComponents/conditionalForm/conditionalFormSection/ConditionalFormSection'),
          import('features/libra/stepComponents/conditionalForm/conditionalFormSection/QuestionListSection'),
        ])
      return {
        default: function FormRenderer({
          config,
          ontologyFields,
          fieldValues,
          setFieldValues,
          stepId,
        }: SectionRenderProps<FormSection>) {
          return config.presentation?.variant === 'question-list' ? (
            <QuestionListSection
              config={config}
              ontologyFields={ontologyFields}
              fieldValues={fieldValues}
              setFieldValues={setFieldValues}
              stepId={stepId}
            />
          ) : (
            <ConditionalFormSectionView
              config={config}
              ontologyFields={ontologyFields}
              virtualObject={fieldValues}
              setVirtualObject={setFieldValues}
              stepId={stepId}
            />
          )
        },
      }
    }),
    completion: (section: FormSection, fieldValues: FieldValues) =>
      (section.requiredFields ?? section.fields).every((field) => {
        const value = unwrapFieldValue(fieldValues[field])
        return (
          value !== undefined &&
          value !== null &&
          (typeof value !== 'string' || value.trim() !== '')
        )
      }),
  },
  flowchart: {
    schema: FlowchartSectionSchema,
    renderer: lazy(async () => {
      const [{ FlowchartSectionView }, { CommentIndicator }] =
        await Promise.all([
          import('features/libra/stepComponents/conditionalForm/flowchart/FlowchartSection'),
          import('features/libra/comments/CommentIndicator'),
        ])
      return {
        default: function FlowchartRenderer({
          config,
          stepId,
          ontologyFields,
          fieldValues,
          initialFieldValues,
          setFieldValues,
        }: SectionRenderProps<FlowchartSection>) {
          if (!setFieldValues) return null
          const fieldId = getFlowchartFieldId(config)
          const fieldDef = ontologyFields[fieldId]
          return (
            <CommentIndicator
              anchor={{ type: 'flowchart', stepId, sectionId: config.id }}>
              <FlowchartSectionView
                section={config}
                fieldId={fieldId}
                fieldDef={
                  fieldDef?.category === FieldCategory.INPUT
                    ? fieldDef
                    : undefined
                }
                ontologyFields={ontologyFields}
                fieldValue={fieldValues[fieldId]}
                savedFieldValue={initialFieldValues[fieldId]}
                detailsValue={
                  config.detailsId ? fieldValues[config.detailsId] : undefined
                }
                setFieldValues={setFieldValues}
              />
            </CommentIndicator>
          )
        },
      }
    }),
    completion: (section: FlowchartSection, fieldValues: FieldValues) => {
      const value = unwrapFieldValue(fieldValues[section.id])
      return value !== undefined && value !== null
    },
  },
  computed: {
    schema: ComputedSectionObjectSchema,
    renderer: lazy(async () => {
      const { ComputedSectionView } =
        await import('features/libra/stepComponents/conditionalForm/computed/ComputedSectionView')
      return {
        default: function ComputedRenderer({
          config,
          fieldValues,
          ontologyFields,
          setFieldValues,
        }: SectionRenderProps<ComputedSection>) {
          return (
            <ComputedSectionView
              config={config}
              fieldValues={fieldValues}
              ontologyFields={ontologyFields}
              setFieldValues={setFieldValues}
              context={{ ontologyFields }}
              readonly={!setFieldValues}
            />
          )
        },
      }
    }),
    completion: (section: ComputedSection, fieldValues: FieldValues) => {
      const value = unwrapFieldValue(fieldValues[getComputedFieldId(section)])
      return value !== undefined && value !== null
    },
  },
  array_table: {
    schema: ArrayTableSectionSchema,
    renderer: lazy(async () => {
      const [{ ArrayTableSectionView }, { CommentIndicator }] =
        await Promise.all([
          import('./arrayTableSection/ArrayTableSectionView'),
          import('features/libra/comments/CommentIndicator'),
        ])
      return {
        default: function ArrayTableRenderer({
          config,
          fieldValues,
          ontologyFields,
          setFieldValues,
          stepId,
        }: SectionRenderProps<ArrayTableSection>) {
          return (
            <CommentIndicator
              anchor={{ type: 'section', stepId, sectionId: config.id }}>
              <ArrayTableSectionView
                config={config}
                fieldValues={fieldValues}
                ontologyFields={ontologyFields}
                setFieldValues={setFieldValues}
              />
            </CommentIndicator>
          )
        },
      }
    }),
    // Tables are optional supporting records; an empty table does not block progression.
    completion: () => true,
  },
  zones: {
    schema: ZonesSectionSchema,
    renderer: lazy(async () => {
      const { ZonesSectionView } =
        await import('./zonesSection/ZonesSectionView')
      return { default: ZonesSectionView }
    }),
    // Data Gathering permits incomplete zones; materials and damage mechanisms are refined later.
    completion: () => true,
  },
  consequence_of_failure: {
    schema: ConsequenceOfFailureSectionSchema,
    renderer: lazy(async () => {
      const { ConsequenceOfFailureSectionView } =
        await import('./consequenceOfFailureSection/ConsequenceOfFailureSectionView')
      return { default: ConsequenceOfFailureSectionView }
    }),
    // An unset CoF falls back to medium, so it never blocks progression.
    completion: () => true,
  },
  document_attachments: {
    schema: DocumentAttachmentsSectionSchema,
    renderer: lazy(async () => {
      const { DocumentAttachmentsSectionView } =
        await import('./documentAttachmentsSection/DocumentAttachmentsSectionView')
      return { default: DocumentAttachmentsSectionView }
    }),
    completion: (
      section: DocumentAttachmentsSection,
      fieldValues: FieldValues
    ) => {
      if (!section.required) return true
      const ids = documentAttachmentIdsSchema.safeParse(
        unwrapFieldValue(fieldValues[section.field])
      )
      return ids.success && ids.data.length > 0
    },
  },
}

export type SectionConfigMap = {
  [Kind in keyof typeof definitions]: z.output<
    (typeof definitions)[Kind]['schema']
  >
}

type SectionRegistry = {
  [Kind in keyof SectionConfigMap]: {
    schema: (typeof definitions)[Kind]['schema']
    renderer: ComponentType<SectionRenderProps<SectionConfigMap[Kind]>>
    completion: (
      section: SectionConfigMap[Kind],
      fieldValues: FieldValues,
      globalContext: LibraWorkflowGlobalContext
    ) => boolean
  }
}

export const sectionRegistry: SectionRegistry = definitions

export function renderSection<Kind extends keyof SectionConfigMap>(
  kind: Kind,
  props: SectionRenderProps<SectionConfigMap[Kind]>
) {
  return createElement(sectionRegistry[kind].renderer, props)
}

export function isSectionComplete<Kind extends keyof SectionConfigMap>(
  kind: Kind,
  section: SectionConfigMap[Kind],
  fieldValues: FieldValues,
  globalContext: LibraWorkflowGlobalContext
): boolean {
  return sectionRegistry[kind].completion(section, fieldValues, globalContext)
}
